import React, { useState, useEffect, useRef } from 'react';
import { Pill, Plus, Trash2, AlertTriangle, Search, X, PackageOpen, ChevronDown, ChevronRight, Building, Archive, FileDown, User, Pencil } from 'lucide-react';
import { getMedProductTypes, getMedBatches, getMedBatchesMultiStructures, createMedBatch, updateMedBatch, deleteMedBatch, checkMedRecall, openMedBox, closeMedBox, getProducts, getChildren } from '../api';
import { useStructure } from '../contexts/StructureContext';
import { usePersonContext } from '../contexts/PersonContext';
import { useToast } from '../contexts/NotificationContext';
import PdfPreviewModal from './ui/PdfPreviewModal';

const MedTraceability = ({ isAdmin = false, canEdit = false, showAllStructures = false }) => {
  const { currentStructure, structures } = useStructure();
  const { currentPerson } = usePersonContext();
  const toast = useToast();
  const isMulti = showAllStructures && structures.length >= 2;
  const [batches, setBatches] = useState([]);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [collapsedStructures, setCollapsedStructures] = useState({});
  const [products, setProducts] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showExpired, setShowExpired] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [recallCheck, setRecallCheck] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [openBatchModal, setOpenBatchModal] = useState(null);
  const [openDate, setOpenDate] = useState('');
  const [editBatch, setEditBatch] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [showPdfFilter, setShowPdfFilter] = useState(false);
  const [pdfFrom, setPdfFrom] = useState('');
  const [pdfTo, setPdfTo] = useState('');
  const [children, setChildren] = useState([]);
  const lotInputRef = useRef(null);

  const [newBatch, setNewBatch] = useState({
    lotNumber: '',
    productTypeId: '',
    productName: '',
    expiryDate: '',
    quantity: 1,
    openDuration: 15,
    notes: '',
    productId: '',
    structureId: '',
    ownerFirstName: '',
  });

  useEffect(() => {
    fetchProductTypes();
  }, []);

  useEffect(() => {
    if (isMulti && newBatch.structureId) {
      getProducts(newBatch.structureId).then(res => setProducts(res.data)).catch(() => {});
      getChildren(newBatch.structureId).then(res => setChildren(res.data)).catch(() => {});
    }
  }, [newBatch.structureId]);

  useEffect(() => {
    if (currentStructure) {
      fetchBatches();
      if (!isMulti) {
        getChildren(currentStructure.id).then(res => setChildren(res.data)).catch(() => {});
      }
    }
  }, [currentStructure, showExpired, showAllStructures]);

  const fetchProductTypes = async () => {
    try {
      const res = await getMedProductTypes();
      setProductTypes(res.data);
    } catch (error) {
      console.error('Error fetching med product types:', error);
      toast.error('Erreur lors du chargement des types de produits');
    }
  };

  const fetchBatches = async () => {
    try {
      setLoading(true);
      if (isMulti) {
        const structureIds = structures.map(s => s.id);
        const res = await getMedBatchesMultiStructures(structureIds);
        setBatches(res.data);
        setProducts([]);
      } else {
        const [batchRes, productRes] = await Promise.all([
          getMedBatches(currentStructure.id, showExpired),
          getProducts(currentStructure.id),
        ]);
        setBatches(batchRes.data);
        setProducts(productRes.data);
      }
    } catch (error) {
      console.error('Error fetching med batches:', error);
      toast.error('Erreur lors du chargement des lots de medicaments');
    } finally {
      setLoading(false);
    }
  };

  const handleTypeChange = (typeId) => {
    const type = productTypes.find((t) => t.id === typeId);
    setNewBatch((prev) => ({
      ...prev,
      productTypeId: typeId,
      openDuration: type ? type.defaultOpenDuration : 15,
    }));
  };

  // Helper : le type sélectionné dans le formulaire utilise-t-il la péremption comme validité ?
  const selectedType = productTypes.find((t) => t.id === newBatch.productTypeId);
  const selectedTypeUsesExpiry = selectedType?.useExpiryAsOpenedValidity || false;

  const handleOpenBox = (batch) => {
    setOpenBatchModal({ batch });
    setOpenDate(new Date().toISOString().split('T')[0]);
  };

  const confirmOpenBox = async () => {
    try {
      await openMedBox(openBatchModal.batch.id, {
        openedBy: currentPerson ? `${currentPerson.firstName} ${currentPerson.lastName}` : null,
        openedAt: openDate,
        personName: currentPerson ? `${currentPerson.firstName} ${currentPerson.lastName}` : null,
        personInitials: currentPerson ? currentPerson.initials : null,
      });
      setOpenBatchModal(null);
      toast.success('Lot ouvert');
      fetchBatches();
    } catch (error) {
      console.error('Error opening med box:', error);
      toast.error('Erreur lors de l\'ouverture du lot');
    }
  };

  const handleExportPdf = async () => {
    const { generateTraceabilityPdf } = await import('../utils/traceabilityPdf');
    const allBatchesRes = await getMedBatches(currentStructure.id, true);
    const result = generateTraceabilityPdf({
      module: 'med',
      batches: allBatchesRes.data,
      structureName: currentStructure.name,
      dateFrom: pdfFrom,
      dateTo: pdfTo,
      preview: true,
    });
    if (result) setPdfPreview(result);
    setShowPdfFilter(false);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newBatch.lotNumber.trim() || !newBatch.productTypeId) return;

    try {
      await createMedBatch({
        ...newBatch,
        structureId: newBatch.structureId || currentStructure.id,
        expiryDate: newBatch.expiryDate || null,
        productId: newBatch.productId || null,
      });

      setNewBatch({
        lotNumber: '',
        productTypeId: '',
        productName: '',
        expiryDate: '',
        quantity: 1,
        openDuration: 15,
        notes: '',
        productId: '',
        structureId: '',
        ownerFirstName: '',
      });
      setShowAddForm(false);
      toast.success('Lot ajoute');
      fetchBatches();
    } catch (error) {
      console.error('Error creating med batch:', error);
      toast.error('Erreur lors de la creation du lot');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce lot ?')) return;
    try {
      await deleteMedBatch(id);
      toast.success('Lot supprime');
      fetchBatches();
    } catch (error) {
      console.error('Error deleting med batch:', error);
      toast.error('Erreur lors de la suppression du lot');
    }
  };

  const handleClose = async (batch) => {
    if (!window.confirm(`Marquer le lot ${batch.lotNumber} comme terminé ?\nUtiliser ce bouton quand le médicament est consommé ou jeté.\nIl disparaîtra de la liste active.`)) return;
    try {
      await closeMedBox(batch.id);
      toast.success('Lot termine');
      fetchBatches();
    } catch (error) {
      console.error('Error closing med batch:', error);
      toast.error('Erreur lors de la fermeture du lot');
    }
  };

  const startEditBatch = (batch) => {
    setEditBatch(batch);
    setEditForm({
      lotNumber: batch.lotNumber || '',
      productTypeId: batch.productTypeId || '',
      productName: batch.productName || '',
      expiryDate: batch.expiryDate ? new Date(batch.expiryDate).toISOString().slice(0, 10) : '',
      openDuration: batch.openDuration || 15,
      quantity: batch.quantity || 1,
      notes: batch.notes || '',
      ownerFirstName: batch.ownerFirstName || '',
      productId: batch.productId || '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editBatch) return;
    setSavingEdit(true);
    try {
      await updateMedBatch(editBatch.id, editForm);
      toast.success('Lot modifie');
      setEditBatch(null);
      fetchBatches();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur modification');
    }
    setSavingEdit(false);
  };

  const handleCheckRecall = async (lotNumber) => {
    try {
      const response = await checkMedRecall(lotNumber);
      setRecallCheck(response.data);
    } catch (error) {
      console.error('Error checking recall:', error);
      toast.error('Erreur lors de la verification de rappel');
    }
  };

  const recalledBatches = batches.filter((b) => b.isRecalled);
  const openedExpiredBatches = batches.filter(
    (b) => b.status === 'opened' && b.openedExpiry && new Date(b.openedExpiry) < new Date()
  );
  const filteredBatches = batches
    .filter(
      (b) =>
        b.lotNumber.toLowerCase().includes(searchText.toLowerCase()) ||
        (b.productType?.name && b.productType.name.toLowerCase().includes(searchText.toLowerCase())) ||
        (b.productName && b.productName.toLowerCase().includes(searchText.toLowerCase())) ||
        (b.ownerFirstName && b.ownerFirstName.toLowerCase().includes(searchText.toLowerCase()))
    )
    .sort((a, b) => {
      const nameA = (a.productName || a.productType?.name || '').toLowerCase();
      const nameB = (b.productName || b.productType?.name || '').toLowerCase();
      if (nameA !== nameB) return nameA.localeCompare(nameB);
      return new Date(b.receivedAt) - new Date(a.receivedAt);
    });

  if (!currentStructure) {
    return (
      <div className="p-8 text-center text-gray-500">
        Sélectionnez une structure pour voir la traçabilité des médicaments.
      </div>
    );
  }

  const toggleStructure = (name) => {
    setCollapsedStructures(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const grouped = {};
  if (isMulti) {
    filteredBatches.forEach(b => {
      const name = structures.find(s => s.id === b.structureId)?.name || 'Structure inconnue';
      if (!grouped[name]) grouped[name] = [];
      grouped[name].push(b);
    });
  }
  const sortedGroupNames = Object.keys(grouped).sort((a, b) => a.localeCompare(b));

  return (
    <div className="container mx-auto p-4 pb-24">
      {/* Recall alert banner */}
      {recalledBatches.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-start gap-3">
          <AlertTriangle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="font-semibold text-red-800">
              {recalledBatches.length} lot(s) rappelé(s) détecté(s) !
            </p>
            {recalledBatches.map((b) => (
              <p key={b.id} className="text-sm text-red-700 mt-1">
                Lot <strong>{b.lotNumber}</strong> ({b.productType?.name})
                {b.recallReason && ` — ${b.recallReason}`}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Opened-expired alert banner */}
      {openedExpiredBatches.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4 flex items-start gap-3">
          <AlertTriangle className="text-orange-500 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="font-semibold text-orange-800">
              {openedExpiredBatches.length} lot(s) ouvert(s) dépassé(s) !
            </p>
            {openedExpiredBatches.map((b) => (
              <p key={b.id} className="text-sm text-orange-700 mt-1">
                Lot <strong>{b.lotNumber}</strong> ({b.productType?.name || b.productName || '—'}) — expiré le {new Date(b.openedExpiry).toLocaleDateString('fr-FR')}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <Pill className="text-primary" size={24} />
          <h2 className="text-xl font-bold text-gray-800">Traçabilité des médicaments{isMulti ? ' — Toutes les structures' : ''}</h2>
          <span className="bg-primary/10 text-primary text-xs font-semibold px-2 py-0.5 rounded-full">
            {batches.length} lots
          </span>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => setShowPdfFilter(true)}
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
          >
            <FileDown size={15} /> Exporter PDF
          </button>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showExpired}
              onChange={(e) => setShowExpired(e.target.checked)}
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
            Afficher les lots expirés
          </label>
        </div>
      </div>

      {/* Modal — ouverture lot */}
      {openBatchModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm mx-4">
            <h3 className="font-semibold text-gray-800 mb-1">Ouvrir le lot</h3>
            <p className="text-sm text-gray-600 mb-4">
              Lot <span className="font-mono font-semibold">{openBatchModal.batch.lotNumber}</span>
              {openBatchModal.batch.product && ` — Stock "${openBatchModal.batch.product.name}" : -1`}
              {openBatchModal.batch.quantity > 1 && (
                <span className="block mt-0.5 text-xs text-gray-500">{openBatchModal.batch.quantity - 1} conditionnement(s) scellé(s) restant(s) après</span>
              )}
            </p>
            {openBatchModal.batch.productType?.useExpiryAsOpenedValidity ? (
              <p className="text-xs text-blue-600 bg-blue-50 px-2 py-1.5 rounded mb-3">
                Valide jusqu'a la date de peremption{openBatchModal.batch.expiryDate ? ` (${new Date(openBatchModal.batch.expiryDate).toLocaleDateString('fr-FR')})` : ''}
              </p>
            ) : (
              <p className="text-xs text-gray-500 mb-3">
                Valide {openBatchModal.batch.openDuration || 15} jour(s) apres ouverture
              </p>
            )}
            {currentPerson && (
              <p className="text-xs text-gray-500 mb-3">Opéré par : <span className="font-medium text-gray-700">{currentPerson.firstName} {currentPerson.lastName}</span></p>
            )}
            <label className="block text-sm font-medium text-gray-700 mb-1">Date d'ouverture</label>
            <input
              type="date"
              value={openDate}
              max={new Date().toISOString().split('T')[0]}
              onChange={(e) => setOpenDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4 focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setOpenBatchModal(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition"
              >
                Annuler
              </button>
              <button
                onClick={confirmOpenBox}
                className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark transition"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal — export PDF */}
      {showPdfFilter && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm mx-4">
            <h3 className="font-semibold text-gray-800 mb-4">Exporter en PDF</h3>
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Du</label>
                <input type="date" value={pdfFrom} onChange={(e) => setPdfFrom(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Au</label>
                <input type="date" value={pdfTo} onChange={(e) => setPdfTo(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none" />
              </div>
              <p className="text-xs text-gray-500">Laisser vide pour exporter tous les lots visibles</p>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowPdfFilter(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition">
                Annuler
              </button>
              <button onClick={handleExportPdf}
                className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark transition">
                Générer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add form */}
      {showAddForm && (
        <form onSubmit={handleAdd} className="bg-white rounded-lg shadow-md p-4 mb-4 border border-primary/20">
          <h3 className="font-semibold text-gray-800 mb-3">Enregistrer un lot médicament</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {isMulti && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Structure *</label>
                <select
                  value={newBatch.structureId}
                  onChange={(e) => setNewBatch({ ...newBatch, structureId: e.target.value, productId: '' })}
                  required
                  className="w-full border border-gray-300 rounded-lg py-2.5 px-3 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
                >
                  <option value="">Sélectionner une structure</option>
                  {structures.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                N° de lot *
              </label>
              <input
                ref={lotInputRef}
                type="text"
                value={newBatch.lotNumber}
                onChange={(e) => setNewBatch({ ...newBatch, lotNumber: e.target.value })}
                placeholder="Ex: MED2026ABC123"
                className="w-full border border-gray-300 rounded-lg py-2.5 px-3 text-lg font-mono focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
                required
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type de produit *
              </label>
              <select
                value={newBatch.productTypeId}
                onChange={(e) => handleTypeChange(e.target.value)}
                className="w-full border border-gray-300 rounded-lg py-2.5 px-3 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
                required
              >
                <option value="">Sélectionner...</option>
                {productTypes.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom commercial
              </label>
              <input
                type="text"
                list="med-product-names"
                value={newBatch.productName}
                onChange={(e) => setNewBatch({ ...newBatch, productName: e.target.value })}
                placeholder="Ex: Doliprane 100mg"
                className="w-full border border-gray-300 rounded-lg py-2.5 px-3 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
              />
              <datalist id="med-product-names">
                {[...new Set(batches.map(b => b.productName).filter(Boolean))].sort().map(name => (
                  <option key={name} value={name} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Enfant
              </label>
              <select
                value={newBatch.ownerFirstName}
                onChange={(e) => setNewBatch({ ...newBatch, ownerFirstName: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-2.5 px-3 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
              >
                <option value="">Aucun (optionnel)</option>
                {children.map((c) => (
                  <option key={c.id} value={c.firstName}>{c.firstName} {c.lastName}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date de péremption
              </label>
              <input
                type="date"
                value={newBatch.expiryDate}
                onChange={(e) => setNewBatch({ ...newBatch, expiryDate: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-2.5 px-3 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Durée après ouverture (jours)
              </label>
              {selectedTypeUsesExpiry ? (
                <p className="text-xs text-blue-600 bg-blue-50 px-3 py-2.5 rounded-lg">
                  Ce type utilise la date de peremption comme validite apres ouverture
                </p>
              ) : (
                <input
                  type="number"
                  min="1"
                  value={newBatch.openDuration}
                  onChange={(e) => setNewBatch({ ...newBatch, openDuration: parseInt(e.target.value) || 15 })}
                  className="w-full border border-gray-300 rounded-lg py-2.5 px-3 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
                />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantité
              </label>
              <input
                type="number"
                min="1"
                value={newBatch.quantity}
                onChange={(e) => setNewBatch({ ...newBatch, quantity: parseInt(e.target.value) || 1 })}
                className="w-full border border-gray-300 rounded-lg py-2.5 px-3 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Produit en stock
              </label>
              <select
                value={newBatch.productId}
                onChange={(e) => setNewBatch({ ...newBatch, productId: e.target.value })}
                className="w-full border border-gray-300 rounded-lg py-2.5 px-3 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
              >
                <option value="">Aucun (optionnel)</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} (stock: {p.currentStock})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <input
                type="text"
                value={newBatch.notes}
                onChange={(e) => setNewBatch({ ...newBatch, notes: e.target.value })}
                placeholder="Optionnel"
                className="w-full border border-gray-300 rounded-lg py-2.5 px-3 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-4">
            <button
              type="submit"
              className="bg-primary text-white px-6 py-2.5 rounded-lg hover:bg-primary-dark transition font-medium"
            >
              Enregistrer le lot
            </button>
            <button
              type="button"
              onClick={() => setShowAddForm(false)}
              className="text-gray-500 hover:text-gray-700 px-4 py-2.5"
            >
              Annuler
            </button>
          </div>
        </form>
      )}

      {/* Search */}
      <div className="relative mb-4">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Rechercher par n° de lot, type, nom..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="w-full border border-gray-300 rounded-lg py-2.5 pl-10 pr-9 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none bg-white"
        />
        {searchText && (
          <button onClick={() => setSearchText('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <X size={16} />
          </button>
        )}
      </div>

      {/* Batches list */}
      {loading ? (
        <div className="text-center py-8 text-gray-400">Chargement...</div>
      ) : isMulti ? (
        sortedGroupNames.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
            <Pill size={48} className="mx-auto mb-3 text-gray-300" />
            <p>Aucun lot de médicament enregistré</p>
          </div>
        ) : (
          <div className="space-y-4">
            {sortedGroupNames.map(structureName => {
              const items = grouped[structureName];
              const isCollapsed = collapsedStructures[structureName];
              const recalledCount = items.filter(b => b.isRecalled).length;
              return (
                <div key={structureName}>
                  <button
                    onClick={() => toggleStructure(structureName)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-primary/10 hover:bg-primary/15 rounded-t-lg transition-colors border-l-4 border-primary"
                  >
                    <div className="flex items-center gap-2">
                      {isCollapsed ? <ChevronRight size={20} className="text-primary" /> : <ChevronDown size={20} className="text-primary" />}
                      <Building size={18} className="text-primary" />
                      <span className="font-bold text-gray-900">{structureName}</span>
                      <span className="text-sm text-gray-500">({items.length} lot{items.length !== 1 ? 's' : ''})</span>
                    </div>
                    {recalledCount > 0 ? (
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                        {recalledCount} rappel{recalledCount > 1 ? 's' : ''}
                      </span>
                    ) : (
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">OK</span>
                    )}
                  </button>
                  {!isCollapsed && (
                    <div className="bg-white rounded-b-lg shadow-md overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="bg-gray-50 text-left text-sm text-gray-600">
                              <th className="px-4 py-3 font-medium">N° de lot</th>
                              <th className="px-4 py-3 font-medium">Type</th>
                              <th className="px-4 py-3 font-medium hidden sm:table-cell">Nom</th>
                              <th className="px-4 py-3 font-medium hidden md:table-cell">Péremption</th>
                              <th className="px-4 py-3 font-medium hidden md:table-cell">Ouverture</th>
                              <th className="px-4 py-3 font-medium">Statut</th>
                              <th className="px-4 py-3 font-medium w-24"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {items.map(batch => {
                              const isExpired = batch.expiryDate && new Date(batch.expiryDate) < new Date();
                              const isOpenedExpired = batch.openedExpiry && new Date(batch.openedExpiry) < new Date();
                              return (
                                <tr key={batch.id} className={`text-sm ${batch.isRecalled ? 'bg-red-50' : isOpenedExpired ? 'bg-orange-50' : isExpired ? 'bg-gray-50 text-gray-400' : ''}`}>
                                  <td className="px-4 py-3 font-mono font-medium">{batch.lotNumber}</td>
                                  <td className="px-4 py-3">{batch.productType ? <span className="text-primary font-medium">{batch.productType.name}</span> : '—'}</td>
                                  <td className="px-4 py-3 hidden sm:table-cell text-gray-500">
                                    {batch.product ? <span className="text-primary font-medium">{batch.product.name}</span> : batch.productName || '—'}
                                  </td>
                                  <td className="px-4 py-3 hidden md:table-cell">
                                    {batch.expiryDate ? new Date(batch.expiryDate).toLocaleDateString('fr-FR') : '—'}
                                  </td>
                                  <td className="px-4 py-3 hidden md:table-cell">
                                    {batch.openedAt ? (
                                      <div>
                                        <span className="text-gray-600">{new Date(batch.openedAt).toLocaleDateString('fr-FR')}</span>
                                        {batch.openedExpiry && (
                                          <span className={`block text-xs ${isOpenedExpired ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                                            Exp: {new Date(batch.openedExpiry).toLocaleDateString('fr-FR')}
                                            {batch.productType?.useExpiryAsOpenedValidity
                                              ? ' (peremption)'
                                              : batch.openDuration ? ` (${batch.openDuration}j)` : ''}
                                          </span>
                                        )}
                                      </div>
                                    ) : batch.productType?.useExpiryAsOpenedValidity ? (
                                      <span className="text-xs text-blue-500">Valide jusqu'a peremption</span>
                                    ) : batch.openDuration ? (
                                      <span className="text-xs text-gray-400">{batch.openDuration}j apres ouverture</span>
                                    ) : '—'}
                                  </td>
                                  <td className="px-4 py-3">
                                    {batch.isRecalled ? (
                                      <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 text-xs font-semibold px-2 py-1 rounded-full"><AlertTriangle size={12} />Rappelé</span>
                                    ) : batch.status === 'opened' ? (
                                      isOpenedExpired ? (
                                        <span className="inline-flex items-center gap-1 bg-orange-100 text-orange-700 text-xs font-semibold px-2 py-1 rounded-full"><AlertTriangle size={12} />Ouvert expiré</span>
                                      ) : <span className="text-xs font-medium text-blue-600">Ouvert</span>
                                    ) : batch.status === 'empty' ? (
                                      <span className="text-xs text-gray-400">Terminé</span>
                                    ) : (
                                      <span className="text-xs text-green-600">{batch.quantity > 1 ? `${batch.quantity} scellés` : 'Scellé'}</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex items-center gap-1">
                                      {batch.status === 'sealed' && !batch.isRecalled && (
                                        <button onClick={() => handleOpenBox(batch)} className="p-1 text-primary hover:text-primary-dark transition" title="Ouvrir (-1 stock)">
                                          <PackageOpen size={16} />
                                        </button>
                                      )}
                                      {batch.status === 'opened' && (
                                        <button onClick={() => handleClose(batch)} className="p-1 text-gray-400 hover:text-green-600 transition" title="Marquer comme terminé">
                                          <Archive size={16} />
                                        </button>
                                      )}
                                      {isAdmin && (
                                        <button onClick={() => handleDelete(batch.id)} className="p-1 text-gray-400 hover:text-red-500 transition" title="Supprimer">
                                          <Trash2 size={16} />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      ) : filteredBatches.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
          <Pill size={48} className="mx-auto mb-3 text-gray-300" />
          <p>Aucun lot de médicament enregistré</p>
          <p className="text-sm mt-1">
            Cliquez sur le bouton + pour commencer la traçabilité.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 text-left text-sm text-gray-600">
                  <th className="px-4 py-3 font-medium">N° de lot</th>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium hidden sm:table-cell">Nom</th>
                  <th className="px-4 py-3 font-medium hidden sm:table-cell">Enfant</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">Péremption</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">Ouverture</th>
                  <th className="px-4 py-3 font-medium">Statut</th>
                  <th className="px-4 py-3 font-medium w-24"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredBatches.map((batch) => {
                  const isExpired = batch.expiryDate && new Date(batch.expiryDate) < new Date();
                  const isExpiringSoon =
                    batch.expiryDate &&
                    !isExpired &&
                    new Date(batch.expiryDate) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
                  const isOpenedExpired = batch.openedExpiry && new Date(batch.openedExpiry) < new Date();

                  return (
                    <tr
                      key={batch.id}
                      className={`text-sm ${
                        batch.isRecalled ? 'bg-red-50' : isOpenedExpired ? 'bg-orange-50' : isExpired ? 'bg-gray-50 text-gray-400' : ''
                      }`}
                    >
                      <td className="px-4 py-3 font-mono font-medium">{batch.lotNumber}</td>
                      <td className="px-4 py-3">
                        {batch.productType ? (
                          <span className="text-primary font-medium">{batch.productType.name}</span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell text-gray-500">
                        {batch.product ? (
                          <span className="text-primary font-medium">{batch.product.name}</span>
                        ) : batch.productName || '—'}
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        {batch.ownerFirstName ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">{batch.ownerFirstName}</span>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        {batch.expiryDate ? (
                          <span className={isExpired ? 'text-gray-400 line-through' : isExpiringSoon ? 'text-orange-600 font-medium' : ''}>
                            {new Date(batch.expiryDate).toLocaleDateString('fr-FR')}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        {batch.openedAt ? (
                          <div>
                            <span className="text-gray-600">{new Date(batch.openedAt).toLocaleDateString('fr-FR')}</span>
                            {batch.openedExpiry && (
                              <span className={`block text-xs ${isOpenedExpired ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                                Exp: {new Date(batch.openedExpiry).toLocaleDateString('fr-FR')}
                                {batch.productType?.useExpiryAsOpenedValidity
                                  ? ' (peremption)'
                                  : batch.openDuration ? ` (${batch.openDuration}j)` : ''}
                              </span>
                            )}
                          </div>
                        ) : batch.productType?.useExpiryAsOpenedValidity ? (
                          <span className="text-xs text-blue-500">Valide jusqu'a peremption</span>
                        ) : batch.openDuration ? (
                          <span className="text-xs text-gray-400">{batch.openDuration}j apres ouverture</span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        {batch.isRecalled ? (
                          <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 text-xs font-semibold px-2 py-1 rounded-full">
                            <AlertTriangle size={12} />
                            Rappelé
                          </span>
                        ) : batch.status === 'opened' ? (
                          isOpenedExpired ? (
                            <span className="inline-flex items-center gap-1 bg-orange-100 text-orange-700 text-xs font-semibold px-2 py-1 rounded-full">
                              <AlertTriangle size={12} />
                              Ouvert expiré
                            </span>
                          ) : (
                            <span className="text-xs font-medium text-blue-600">Ouvert</span>
                          )
                        ) : batch.status === 'empty' ? (
                          <span className="text-xs text-gray-400">Terminé</span>
                        ) : isExpired ? (
                          <span className="text-xs text-gray-400">Expiré</span>
                        ) : isExpiringSoon ? (
                          <span className="text-xs text-orange-600 font-medium">Expire bientôt</span>
                        ) : (
                          <span className="text-xs text-green-600">
                            {batch.quantity > 1 ? `${batch.quantity} scellés` : 'Scellé'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {batch.status === 'sealed' && canEdit && (
                            <button onClick={() => startEditBatch(batch)} className="p-1 text-gray-400 hover:text-blue-600 transition" title="Modifier le lot">
                              <Pencil size={14} />
                            </button>
                          )}
                          {batch.status === 'sealed' && !batch.isRecalled && (
                            <button
                              onClick={() => handleOpenBox(batch)}
                              className="p-1 text-primary hover:text-primary-dark transition"
                              title="Ouvrir (-1 stock)"
                            >
                              <PackageOpen size={16} />
                            </button>
                          )}
                          {batch.status === 'opened' && (
                            <button
                              onClick={() => handleClose(batch)}
                              className="p-1 text-gray-400 hover:text-green-600 transition"
                              title="Marquer comme terminé (consommé ou jeté)"
                            >
                              <Archive size={16} />
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              onClick={() => handleDelete(batch.id)}
                              className="p-1 text-gray-400 hover:text-red-500 transition"
                              title="Supprimer"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal edition lot scelle */}
      {editBatch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Pencil size={16} className="text-blue-600" /> Modifier le lot {editBatch.lotNumber}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-xs text-gray-500 mb-1">N° de lot *</label>
                <input type="text" value={editForm.lotNumber} onChange={e => setEditForm(f => ({ ...f, lotNumber: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Type de produit *</label>
                <select value={editForm.productTypeId} onChange={e => setEditForm(f => ({ ...f, productTypeId: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none">
                  <option value="">Selectionner...</option>
                  {productTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Nom commercial</label>
                <input type="text" list="med-edit-names" value={editForm.productName} onChange={e => setEditForm(f => ({ ...f, productName: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none" />
                <datalist id="med-edit-names">{[...new Set(batches.map(b => b.productName).filter(Boolean))].sort().map(n => <option key={n} value={n} />)}</datalist>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Date de peremption</label>
                <input type="date" value={editForm.expiryDate} onChange={e => setEditForm(f => ({ ...f, expiryDate: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Duree apres ouverture (jours)</label>
                <input type="number" min="1" value={editForm.openDuration} onChange={e => setEditForm(f => ({ ...f, openDuration: parseInt(e.target.value) || 15 }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Quantite</label>
                <input type="number" min="1" value={editForm.quantity} onChange={e => setEditForm(f => ({ ...f, quantity: parseInt(e.target.value) || 1 }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Enfant</label>
                <select value={editForm.ownerFirstName} onChange={e => setEditForm(f => ({ ...f, ownerFirstName: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none">
                  <option value="">Aucun</option>
                  {children.map(c => <option key={c.id} value={c.firstName}>{c.firstName} {c.lastName}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Produit en stock</label>
                <select value={editForm.productId} onChange={e => setEditForm(f => ({ ...f, productId: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none">
                  <option value="">Aucun</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.name} (stock: {p.currentStock})</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs text-gray-500 mb-1">Notes</label>
                <input type="text" value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setEditBatch(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
              <button onClick={handleSaveEdit} disabled={savingEdit || !editForm.lotNumber}
                className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50">
                {savingEdit ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FAB */}
      <button
        onClick={() => {
          setShowAddForm(true);
          setTimeout(() => lotInputRef.current?.focus(), 100);
        }}
        className="fixed bottom-6 right-6 z-50 flex items-center justify-center w-14 h-14 bg-primary text-white rounded-full shadow-lg hover:bg-primary-dark transition-all hover:scale-110"
        title="Nouveau lot"
      >
        <Plus size={26} />
      </button>

      {pdfPreview && (
        <PdfPreviewModal
          doc={pdfPreview.doc}
          fileName={pdfPreview.fileName}
          onClose={() => setPdfPreview(null)}
        />
      )}
    </div>
  );
};

export default MedTraceability;
